const STORAGE_KEY = "intended_visit_link";
const ANCHORING_DEAL_STORAGE_KEY = "anchoring_deal_link";
const REDIRECT_PARAM = "redirect";
const LOGIN_PATH = "/app/login";
const SIGNUP_PATH = "/app/signup";
const DEAL_PAGE_PATTERN = /^\/app\/deals\/[A-Za-z0-9_-]+\/?$/;

const AUTH_PATHS = [
  "/app/mfa",
  "/app/login",
  "/app/signup",
  "/app/signup-apple",
  "/app/verify-email",
  "/app/reset-password",
  "/app/forgot-password",
];

const isBrowser = (): boolean => typeof window !== "undefined";

// An absolute URL here would turn `?redirect=` into an open redirect.
const toSafeDestination = (value?: string | null): string | null => {
  if (!isBrowser() || !value) {
    return null;
  }

  try {
    const { origin, pathname, search, hash } = new URL(
      value,
      window.location.origin
    );
    const isInsideApp =
      origin === window.location.origin && pathname.startsWith("/app/");
    const isAuthPath = AUTH_PATHS.some((path) => pathname.startsWith(path));

    return isInsideApp && !isAuthPath ? `${pathname}${search}${hash}` : null;
  } catch {
    return null;
  }
};

const readStored = (key = STORAGE_KEY): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private mode and blocked site data deny storage access.
    return null;
  }
};

const writeStored = (destination: string, key = STORAGE_KEY): void => {
  try {
    window.localStorage.setItem(key, destination);
  } catch {
    // Private mode and blocked site data deny storage access.
  }
};

const clearStored = (key = STORAGE_KEY): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Private mode and blocked site data deny storage access.
  }
};

/** Only a single deal's page can anchor onboarding: never the list or a sub-page. */
export const toAnchoringDealPath = (value?: string | null): string | null => {
  const destination = toSafeDestination(value);
  if (!destination) {
    return null;
  }

  const { pathname } = new URL(destination, window.location.origin);

  return DEAL_PAGE_PATTERN.test(pathname) ? pathname.replace(/\/$/, "") : null;
};

export const rememberAnchoringDeal = (value?: string | null): void => {
  const dealPath = toAnchoringDealPath(value);

  if (dealPath) {
    writeStored(dealPath, ANCHORING_DEAL_STORAGE_KEY);
  }
};

export const peekAnchoringDeal = (): string | null =>
  isBrowser()
    ? toAnchoringDealPath(readStored(ANCHORING_DEAL_STORAGE_KEY))
    : null;

export const forgetAnchoringDeal = (): void => {
  if (isBrowser()) {
    clearStored(ANCHORING_DEAL_STORAGE_KEY);
  }
};

const redirectParam = (): string | null =>
  isBrowser()
    ? new URLSearchParams(window.location.search).get(REDIRECT_PARAM)
    : null;

export const rememberIntendedVisit = (): void => {
  const destination = toSafeDestination(
    isBrowser() ? window.location.href : null
  );

  if (destination) {
    writeStored(destination);
  }
};

export const forgetIntendedVisit = (): void => {
  if (isBrowser()) {
    clearStored();
  }
};

// Read and forget together: one owner, and no login inherits the last one.
export const takeIntendedVisit = (): string | null => {
  if (!isBrowser()) {
    return null;
  }

  const stored = readStored();
  forgetIntendedVisit();

  return toSafeDestination(redirectParam()) || toSafeDestination(stored);
};

export const loginPathWithRedirect = (): string => {
  // A repeat sign-out fires from the login page, where only the query has it.
  const destination =
    toSafeDestination(isBrowser() ? window.location.href : null) ||
    toSafeDestination(redirectParam());

  if (!destination) {
    return LOGIN_PATH;
  }

  const params = new URLSearchParams({ [REDIRECT_PARAM]: destination });

  return `${LOGIN_PATH}?${params.toString()}`;
};

/** Anchors onboarding to the deal a sign-up was sent to, if it was a deal. */
export const anchorIntendedVisit = (): void => {
  if (!isBrowser()) {
    return;
  }

  rememberAnchoringDeal(
    toSafeDestination(redirectParam()) || toSafeDestination(readStored())
  );
};

/** The login page's sign-up link keeps the destination the login page owed. */
export const signupPathWithRedirect = (): string => {
  const destination =
    toSafeDestination(redirectParam()) ||
    (isBrowser() ? toSafeDestination(readStored()) : null);

  if (!destination) {
    return SIGNUP_PATH;
  }

  const params = new URLSearchParams({ [REDIRECT_PARAM]: destination });

  return `${SIGNUP_PATH}?${params.toString()}`;
};
